import {
    GridColDef,
    GridFilterModel,
    GridRenderCellParams,
    GridRowScrollEndParams,
    getGridSingleSelectOperators,
    useGridApiRef,
} from "@mui/x-data-grid-premium";
import React, { FC, useCallback, useState } from "react";
import Loader from "src/components/Loader/Loader";
import { useAppDispatch, useAppSelector } from "src/hooks";
import GlobalStateActions from "src/redux/slices/GlobalStateActions";
import { AllocatorJob, FixMeLater, REPORT_COLUMN_TYPES } from "src/types";
import JobCard from "../JobCard/JobCard";
import { StyledJobList } from "./JobListGrid.styled";
import JobListToolbar from "./components/JobListToolbar/JobListToolbar";
import colors from "src/styles/colors.scss";
import { JOB_STATUS_OPTIONS, QUARTER_OPTIONS, STATE_OPTIONS } from "../../constant";
import NoJobsOverlay from "./components/NoDataOverlay/NoJobsOverlay";

interface JobListGridProps {
    jobList: AllocatorJob[];
    page: number;
    totalPages: number;
    isLoading: boolean;
    isDragActive: boolean;
}

const JobListGrid: FC<JobListGridProps> = ({
    jobList,
    page,
    totalPages,
    isLoading,
    isDragActive
}) => {
    const [isInputSearchFilled, setIsInputSearchFilled] = useState<boolean>(false);
    const singleSelectOperators = getGridSingleSelectOperators().filter(operator => operator.value === "is");
    const columns: GridColDef[] = [
        {
            field: "source.filename",
            headerName: "File Name",
            sortable: false,
            minWidth: 220,
            renderCell: (params: GridRenderCellParams) => (
                <JobCard job={params?.row} />
            ),
            valueGetter: (value, row) =>
                row?.filename,
        },
        {
            field: "source.state",
            headerName: "State",
            type: REPORT_COLUMN_TYPES.SINGLE_SELECT,
            valueOptions: STATE_OPTIONS,
            filterOperators: singleSelectOperators,
        },
        {
            field: "source.quarter",
            headerName: "Quarter",
            type: REPORT_COLUMN_TYPES.SINGLE_SELECT,
            valueOptions: QUARTER_OPTIONS,
            filterOperators: singleSelectOperators,
        },
        {
            field: "source.year",
            headerName: "Year",
            type: REPORT_COLUMN_TYPES.SINGLE_SELECT,
            valueOptions: Array.from({ length: new Date().getFullYear() - 2019}, (_, i) => (new Date().getFullYear() - i).toString()),
            filterOperators: singleSelectOperators,
        },
        {
            field: "status",
            headerName: "Status",
            type: REPORT_COLUMN_TYPES.SINGLE_SELECT,
            valueOptions: JOB_STATUS_OPTIONS,
            filterOperators: singleSelectOperators,
        },
        {
            field: "approvedDate",
            headerName: "Approval Date",
            type: REPORT_COLUMN_TYPES.DATE,
        },
        {
            field: "completionDate",
            headerName: "Completion Date",
            type: REPORT_COLUMN_TYPES.DATE,
        },

    ];

    const apiRef = useGridApiRef();

    const product: FixMeLater = useAppSelector(
        (state) => state?.Product?.value
    );
    const isJobSearchActive: boolean = useAppSelector(
        (state) => state?.[product?.productName]?.value?.jobs?.isJobSearchActive
    );

    let timeout: ReturnType<typeof setTimeout>;

    const dispatch = useAppDispatch();

    const handleOnRowsScrollEnd = (params: GridRowScrollEndParams) => {
        if (params?.visibleRowsCount && !isLoading && page < totalPages) {
            dispatch(
                GlobalStateActions[product?.productName].setJobsPage(page + 1)
            );
        }
    };

    const onFilterChange = useCallback((filterModel: GridFilterModel) => {
        clearTimeout(timeout);

        const filters = filterModel?.items?.filter(item => item?.value?.length || item?.operator === "isEmpty" || item?.operator === "isNotEmpty")
        .map(item => {
            return {
                field: item?.field,
                value: Array.isArray(item?.value)? JSON.stringify(item?.value) : item?.value,
                operator: item?.operator,
                booleanOperator: filterModel?.logicOperator?.toUpperCase()
            }
        })

        const searchPhrase = filterModel?.quickFilterValues?.join(" ");
        setIsInputSearchFilled(!!searchPhrase?.length);
        
        timeout = setTimeout(() => {
            dispatch(
                GlobalStateActions[product?.productName].setJobsFilterState({ filters, searchPhrase: searchPhrase?.length !== 1 ? searchPhrase : ""})
            );
            dispatch(
                GlobalStateActions[product?.productName].setJobsPage(0)
            );
            apiRef.current.scrollToIndexes({rowIndex: 0, colIndex: 0});
        }, 1000);
    }, []);

    if (isLoading && !jobList?.length) return <Loader />;

    return (
        <StyledJobList
            apiRef={apiRef}
            rowHeight={130}
            rows={!isDragActive? jobList : []}
            columnHeaderHeight={0}
            columns={columns}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnReorder
            disableVirtualization
            hideFooter
            filterMode="server"
            initialState={{
                columns: {
                    columnVisibilityModel: {
                        "source.state": false,
                        "source.quarter": false,
                        "source.year": false,
                        "status": false,
                        "approvedDate": false,
                        "completionDate": false,
                    },
                },
            }}
            localeText={{
                toolbarFilters: isJobSearchActive? "" : "Filters",
            }}
            onFilterModelChange={onFilterChange}
            onRowsScrollEnd={handleOnRowsScrollEnd}
            slots={{
                toolbar: JobListToolbar,
                noRowsOverlay: NoJobsOverlay,
                noResultsOverlay: NoJobsOverlay,
            }}
            slotProps={{
                baseSelect: {
                    native: false
                },
                toolbar: {
                    isInputSearchFilled,
                    isDragActive,
                },
                filterPanel: {
                    sx: {
                        "& .MuiDataGrid-panelContent": { maxHeight: "300px" },
                        "& .MuiFormLabel-root.Mui-focused": { color: colors.primaryColor },
                        "& .MuiInput-root:after": {borderBottom: `1px solid ${colors.primaryColor}`},
                        "& .MuiButton-root": { color: colors.primaryColor },
                        "& .MuiInput-root": { minWidth: 120 },
                    },
                }
            }}
        />
    );
};

export default JobListGrid;
